import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../utils/cn';

export type BreadcrumbItemProps = {
    href?: string;
    className?: string;
    children: React.ReactNode;
};

function BreadcrumbItem({ href, className, children }: BreadcrumbItemProps) {
    if (href) {
        return (
            <Link to={href} className={cn('inline-flex items-center gap-2 text-sm', className)}>
                {children}
            </Link>
        );
    }

    return <span className={cn('inline-flex items-center gap-2 text-sm', className)}>{children}</span>;
}

export type BreadcrumbProps = React.HTMLAttributes<HTMLDivElement> & {
    separator?: React.ReactNode;
    disableCurrent?: boolean;
    children: React.ReactNode;
    separatorClassName?: string;
    separatorVariant?: 'default' | 'circle';
};

export default function Breadcrumb({
    separator = '/',
    disableCurrent = true,
    children,
    className,
    separatorClassName,
    separatorVariant = 'default',
    ...props
}: BreadcrumbProps) {
    const items = React.Children.toArray(children).filter(React.isValidElement);
    const numOfItems = items.length;

    return (
        <div className={cn('inline-flex flex-wrap items-center gap-2.5', className)} {...props}>
            {items.map((child, index) => (
                <React.Fragment key={index}>
                    {React.cloneElement(child as React.ReactElement<BreadcrumbItemProps>, {
                        className: cn(
                            'font-medium text-gray-700 last:text-gray-500 dark:text-gray-300 dark:last:text-gray-500',
                            disableCurrent && 'last:pointer-events-none',
                            (child as React.ReactElement<BreadcrumbItemProps>).props.className,
                        ),
                    })}
                    {index < numOfItems - 1 &&
                        (separatorVariant === 'default' ? (
                            <span className={cn('text-sm text-gray-500 dark:text-gray-600', separatorClassName)}>{separator}</span>
                        ) : (
                            <span className="h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-600" />
                        ))}
                </React.Fragment>
            ))}
        </div>
    );
}

Breadcrumb.Item = BreadcrumbItem;
